import {
  START_LOCATION,
  type LocationQuery,
  type NavigationGuardReturn,
  type RouteLocationNormalized,
  type RouteLocationRaw,
} from 'vue-router';
import { DocumentType, isNotFoundError, useCustomToast } from '@/shared/lib';
import { fetchHomologationMatrixStage, fetchHomologationRevisionStage } from '../api';
import {
  DOCUMENT_NOT_IN_HOMOLOGATION_MESSAGE,
  RECORD_NOT_IN_HOMOLOGATION_MESSAGE,
} from '../config';

function parseId(value: unknown): number | null {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return null;
  }

  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

interface StageLookup {
  fetchStage: (id: number) => Promise<{ inHomologation: boolean; documentType: DocumentType }>;
  // Known before asking the server, so a missing item still gets the right message.
  documentType: DocumentType | null;
}

type StageQueryKey = 'revisionId' | 'matrixId';

// A matrix and a document revision can share an id, so each notice names its own query key.
const STAGE_LOOKUPS: Record<StageQueryKey, StageLookup> = {
  revisionId: { fetchStage: fetchHomologationRevisionStage, documentType: null },
  matrixId: {
    fetchStage: async (id) => ({
      inHomologation: (await fetchHomologationMatrixStage(id)).inHomologation,
      documentType: DocumentType.REGISTRATION,
    }),
    documentType: DocumentType.REGISTRATION,
  },
};

const STAGE_QUERY_KEYS = Object.keys(STAGE_LOOKUPS) as StageQueryKey[];

function withoutStageQuery(
  to: RouteLocationNormalized,
  extraQuery: LocationQuery = {}
): RouteLocationRaw {
  const query: LocationQuery = { ...to.query, ...extraQuery };
  STAGE_QUERY_KEYS.forEach((key) => delete query[key]);
  return { path: to.path, query, hash: to.hash };
}

function warnNotInHomologation(documentType: DocumentType | null): void {
  useCustomToast().showWarningToast({
    title: 'Aviso',
    message:
      documentType === DocumentType.REGISTRATION
        ? RECORD_NOT_IN_HOMOLOGATION_MESSAGE
        : DOCUMENT_NOT_IN_HOMOLOGATION_MESSAGE,
    type: 'warning',
  });
}

/**
 * Validates the `revisionId` or `matrixId` a Homologation notice carries before the list opens, so
 * an item already decided by someone else warns instead of opening a list without it.
 */
export async function homologationRevisionStageGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): Promise<NavigationGuardReturn> {
  const key = STAGE_QUERY_KEYS.find((candidate) => candidate in to.query);
  if (!key) {
    return true;
  }

  const id = parseId(to.query[key]);
  if (id === null) {
    return withoutStageQuery(to);
  }

  const lookup = STAGE_LOOKUPS[key];
  let documentType = lookup.documentType;

  try {
    const stage = await lookup.fetchStage(id);

    if (stage.inHomologation) {
      return withoutStageQuery(to, { type: stage.documentType });
    }

    documentType = stage.documentType;
  } catch (error) {
    if (!isNotFoundError(error)) {
      return withoutStageQuery(to);
    }
  }

  warnNotInHomologation(documentType);

  // Aborting the very first navigation would leave a blank screen, since there is no page to stay on.
  return from === START_LOCATION ? withoutStageQuery(to) : false;
}
